import useTranslation from 'next-translate/useTranslation'
import React, { useEffect, useState } from 'react'

import { useAuthentication } from '@/context/AuthProvider'
import { useBottomSheet } from '@/context/BottomSheetContext'
import { useCities } from '@/context/Cities'
import { useCityPage } from '@/context/CityPageContext'
import { useDevice } from '@/context/Device'
import { useModal } from '@/context/ModalContext'
import { ToastError } from '@/design-lib/components/Toast/ToastError'
import api from '@/lib/api-gluglu-auth'
import { GeoFilter } from '@/types/filter'
import { Filter } from '@/types/graphql-generated-types'
import { isStorageSupported } from '@/utils/isStorageSupported'
import { handleNewListingsAlertClick, handleShow, handleSystem } from '@/utils/tracking/eventsHandlers'

import { createPayload } from '../../Form/createPayload'
import { ContentDefault } from '../Common/ContentDefault'
import { ContentLoader, FORCED_LOADING_TIME } from '../Common/ContentLoader'
import { ContentLogin } from '../Common/ContentLogin'

import { ContentSuccess } from './ContentSuccess'

type Props = {
  actualZoneFilterAreas: GeoFilter[]
  filters: Filter
  geoSlugs: string[]
  onSaveSuccess: VoidFunction
}

const SaveSearch: React.FC<Props> = ({ actualZoneFilterAreas, filters, geoSlugs, onSaveSuccess }) => {
  const { saveSearchButtonState } = useCityPage()
  const [isLoadingFavoriteSearch, setIsLoadingFavouriteSearch] = useState(false)
  const [showContentLogin, setShowContentLogin] = useState(false)
  const { isLoading: isLoadingUser, user } = useAuthentication()
  const [error, setError] = useState(false)
  const [showInvalidEmailErrorMessage, setShowInvalidEmailErrorMessage] = useState<boolean>(false)
  const [showGenericErrorMessage, setShowGenericErrorMessage] = useState<boolean>(false)
  const device = useDevice()
  const cities = useCities()
  const { t } = useTranslation()
  const { closeModal } = useModal()
  const { closeBottomSheet } = useBottomSheet()

  const handleSubmitUserFavoriteSearch = async () => {
    const favoriteSearchPayload = createPayload({
      actualZoneFilterAreas,
      filters,
      geoSlugs,
      origin: 'SEARCH_RESULTS',
      referenceCitySlug: cities.router.area?.slug,
    })

    try {
      setIsLoadingFavouriteSearch(true)
      setShowContentLogin(false)
      closeBottomSheet()
      await api.submitUserFavoriteSearch(favoriteSearchPayload)
      handleNewListingsAlertClick('ActivateAlert', 'ListingsFeed')
      setTimeout(() => {
        onSaveSuccess()
      }, FORCED_LOADING_TIME)
    } catch (error) {
      handleSystem('NewListingsAlert', 'ActivateAlertError')
      if (error.message === 'INVALID_EMAIL') {
        handleShow('AuthError', 'InvalidEmail')
        setShowInvalidEmailErrorMessage(true)
      } else {
        handleShow('AuthError', 'GenericError')
        setShowGenericErrorMessage(true)
      }

      setError(true)
    } finally {
      setTimeout(() => {
        setIsLoadingFavouriteSearch(false)
      }, FORCED_LOADING_TIME)
    }
  }

  useEffect(() => {
    async function run() {
      handleSubmitUserFavoriteSearch()
    }

    if (
      !isLoadingUser &&
      user &&
      isStorageSupported(() => sessionStorage) &&
      sessionStorage.getItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
    ) {
      run().finally(() => {
        sessionStorage.removeItem('CASAVO@LISTINGS-SAVE-SEARCH-AFTER-LOGIN')
      })
    }
  }, [user, isLoadingUser])

  if (device === 'mobile') {
    return saveSearchButtonState === 'default' && !isLoadingFavoriteSearch ? (
      <>
        <ContentDefault
          actualZoneFilterAreas={actualZoneFilterAreas}
          cities={cities}
          filters={filters}
          geoSlugs={geoSlugs}
          handleSubmitUserFavoriteSearch={handleSubmitUserFavoriteSearch}
          origin="SEARCH_RESULTS"
          setIsLoadingFavouriteSearch={setIsLoadingFavouriteSearch}
        />
        {showInvalidEmailErrorMessage && (
          <ToastError
            description={t('save-search:Error_InvalidMail_Description')}
            error={error}
            setError={setError}
            title=""
          />
        )}
        {showGenericErrorMessage && <ToastError error={error} setError={setError} />}
      </>
    ) : isLoadingFavoriteSearch ? (
      <ContentLoader />
    ) : (
      <ContentSuccess
        actualZoneFilterAreas={actualZoneFilterAreas}
        isSearchSaved={saveSearchButtonState !== 'default'}
      />
    )
  } else {
    return saveSearchButtonState === 'default' && !isLoadingFavoriteSearch && !showContentLogin ? (
      <>
        <ContentDefault
          actualZoneFilterAreas={actualZoneFilterAreas}
          cities={cities}
          filters={filters}
          geoSlugs={geoSlugs}
          handleSubmitUserFavoriteSearch={handleSubmitUserFavoriteSearch}
          origin="SEARCH_RESULTS"
          setIsLoadingFavouriteSearch={setIsLoadingFavouriteSearch}
          setShowContentLogin={setShowContentLogin}
        />
        {showInvalidEmailErrorMessage && (
          <ToastError
            description={t('save-search:Error_InvalidMail_Description')}
            error={error}
            setError={setError}
            title={t('save-search:Error_InvalidMail_Title')}
          />
        )}
        {showGenericErrorMessage && <ToastError error={error} setError={setError} />}
      </>
    ) : isLoadingFavoriteSearch ? (
      <ContentLoader />
    ) : showContentLogin ? (
      <ContentLogin
        actualZoneFilterAreas={actualZoneFilterAreas}
        filters={filters}
        geoSlugs={geoSlugs}
        origin="SEARCH_RESULTS"
        setIsLoadingFavouriteSearch={setIsLoadingFavouriteSearch}
        onLoggedIn={() => closeModal()}
      />
    ) : (
      <ContentSuccess
        actualZoneFilterAreas={actualZoneFilterAreas}
        isSearchSaved={saveSearchButtonState !== 'default'}
      />
    )
  }
}

export default SaveSearch
